import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { processHistoricalSearchResults } from '../lib/historical-search-processor';
import { useAuth } from '../lib/auth.context';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Define the section order to match KYPSearch.tsx
const SECTIONS_ORDER = [
  'family',
  'financial',
  'career',
  'personal_life',
  'agent',
  'personality',
  'early_career_and_education',
  'adverse_media',
  'legal_and_criminal_history',
  'sponsorships'
];

interface HistoricalSearchDialogProps {
  searchData?: any;
  searchId?: number;
  onClose: () => void;
}

export const HistoricalSearchDialog: React.FC<HistoricalSearchDialogProps> = ({
  searchData,
  searchId,
  onClose
}) => {
  const { getToken } = useAuth();
  const [processedData, setProcessedData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchHistory, setSearchHistory] = useState<any[]>([]);
  const [currentSearchData, setCurrentSearchData] = useState<any>(null);
  
  // Process the data when the component mounts
  useEffect(() => {
    if (searchData) {
      try {
        // Remove status and message fields
        const { status, message, player_id, ...playerDataFields } = searchData;
        
        // Process the data
        const processed = processHistoricalSearchResults(playerDataFields);
        setProcessedData(processed);
      } catch (e) {
        console.error('Error processing historical search data:', e);
        // Fallback to using raw data if processing fails
        setProcessedData(searchData);
      }
    } else if (searchId) {
      // If searchId is provided, fetch the search data
      fetchSearchData(searchId);
    } else {
      // If neither searchData nor searchId is provided, fetch search history
      fetchSearchHistory();
    }
  }, [searchData, searchId]);
  
  // Fetch search history when page changes
  useEffect(() => {
    if (!searchData && !searchId) {
      fetchSearchHistory();
    }
  }, [page]);
  
  // When a search is selected from history, process it
  useEffect(() => {
    if (currentSearchData) {
      try {
        // Process the data
        const processed = processHistoricalSearchResults(currentSearchData);
        setProcessedData(processed);
      } catch (e) {
        console.error('Error processing historical search data:', e);
        // Fallback to using raw data if processing fails
        setProcessedData(currentSearchData);
      }
    }
  }, [currentSearchData]);
  
  const fetchSearchHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getToken();
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }
      
      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const pageSize = 10; // Number of searches per page
      
      const response = await fetch(
        `${baseUrl}/ai/v1/user/search/history?page=${page}&page_size=${pageSize}&sort_by=created_at&sort_order=desc&include_automated=false`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status === 'success') {
        // Get the search history
        const searchHistoryData = data.search_history || [];
        
        // For completed searches, fetch the overall player score
        const searchHistoryWithScores = await Promise.all(
          searchHistoryData.map(async (search: any) => {
            // Only fetch scores for completed searches
            if (search.status === 'completed') {
              try {
                console.log(`Fetching score for search ${search.id}`);
                const scoreResponse = await fetch(`${baseUrl}/ai/v1/player/search/async/${search.id}/results`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                if (scoreResponse.ok) {
                  const scoreData = await scoreResponse.json();
                  console.log(`Score data for search ${search.id}:`, scoreData);
                  
                  if (scoreData.status === 'success') {
                    // Try to extract overall_player_score from the response
                    if (scoreData.overall_player_score !== undefined) {
                      console.log(`Found overall_player_score: ${scoreData.overall_player_score}`);
                      return {
                        ...search,
                        overall_player_score: scoreData.overall_player_score
                      };
                    } else {
                      // If overall_player_score is not directly in the response, try to process the data
                      try {
                        const { status, message, player_id, ...playerDataFields } = scoreData;
                        const processed = processHistoricalSearchResults(playerDataFields);
                        
                        if (processed.overall_player_score !== undefined) {
                          console.log(`Extracted overall_player_score from processed data: ${processed.overall_player_score}`);
                          return {
                            ...search,
                            overall_player_score: processed.overall_player_score
                          };
                        }
                      } catch (processingError) {
                        console.error(`Error processing score data for search ${search.id}:`, processingError);
                      }
                    }
                  }
                }
              } catch (error) {
                console.error(`Error fetching score for search ${search.id}:`, error);
              }
            }
            return search;
          })
        );
        
        console.log("Search history with scores:", searchHistoryWithScores);
        setSearchHistory(searchHistoryWithScores);
        
        // Set total pages from pagination info
        if (data.pagination) {
          setTotalPages(data.pagination.total_pages || 1);
        }
        
        // If we have search history and no current search data, select the first one
        if (searchHistoryWithScores.length > 0 && !currentSearchData) {
          // Get the first completed search
          const firstCompletedSearch = searchHistoryWithScores.find((search: any) => search.status === 'completed');
          if (firstCompletedSearch) {
            fetchSearchData(firstCompletedSearch.id);
          }
        }
      } else {
        setSearchHistory([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching search history:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch search history');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSearchData = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getToken();
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }
      
      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      const response = await fetch(`${baseUrl}/ai/v1/player/search/async/${id}/results`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status === 'success') {
        // Remove status and message fields
        const { status, message, player_id, ...playerDataFields } = data;
        
        // Set the current search data
        setCurrentSearchData(playerDataFields);
      } else if (data.status === 'pending') {
        setError('Search is still in progress');
      } else {
        setError('Failed to fetch search results');
      }
    } catch (error) {
      console.error('Error fetching search data:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch search results');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
      setCurrentSearchData(null);
    }
  };
  
  const handleSearchSelect = (search: any) => {
    if (search.status === 'completed') {
      fetchSearchData(search.id);
    }
  };
  
  // Helper function to extract rating data from various possible structures
  const extractRatingData = (data: any): { rating: number; explanation: string } | null => {
    if (!data) return null;
    
    // Case 1: Check for summary.rating structure (most common in current searches)
    if (data.summary && typeof data.summary === 'object' && data.summary.rating) {
      const ratingObj = data.summary.rating;
      if (typeof ratingObj === 'object') {
        if ('score' in ratingObj) {
          return {
            rating: Number(ratingObj.score),
            explanation: ratingObj.explanation || 'No explanation provided'
          };
        } else if ('rating' in ratingObj) {
          return {
            rating: Number(ratingObj.rating),
            explanation: ratingObj.explanation || 'No explanation provided'
          };
        }
      } else if (typeof ratingObj === 'number') {
        return {
          rating: ratingObj,
          explanation: 'No explanation provided'
        };
      } else if (typeof ratingObj === 'string') {
        try {
          const parsed = JSON.parse(ratingObj);
          if (typeof parsed === 'object') {
            if ('score' in parsed) {
              return {
                rating: Number(parsed.score),
                explanation: parsed.explanation || 'No explanation provided'
              };
            } else if ('rating' in parsed) {
              return {
                rating: Number(parsed.rating),
                explanation: parsed.explanation || 'No explanation provided'
              };
            }
          } else if (typeof parsed === 'number') {
            return {
              rating: parsed,
              explanation: 'No explanation provided'
            };
          }
        } catch (e) {
          // If parsing fails, try to use the string directly as a number
          const numRating = Number(ratingObj);
          if (!isNaN(numRating)) {
            return {
              rating: numRating,
              explanation: 'No explanation provided'
            };
          }
        }
      }
    }
    
    // Case 2: Check for direct rating property
    if (data.rating) {
      const ratingObj = data.rating;
      if (typeof ratingObj === 'object') {
        if ('score' in ratingObj) {
          return {
            rating: Number(ratingObj.score),
            explanation: ratingObj.explanation || 'No explanation provided'
          };
        } else if ('rating' in ratingObj) {
          return {
            rating: Number(ratingObj.rating),
            explanation: ratingObj.explanation || 'No explanation provided'
          };
        }
      } else if (typeof ratingObj === 'number') {
        return {
          rating: ratingObj,
          explanation: 'No explanation provided'
        };
      } else if (typeof ratingObj === 'string') {
        try {
          const parsed = JSON.parse(ratingObj);
          if (typeof parsed === 'object') {
            if ('score' in parsed) {
              return {
                rating: Number(parsed.score),
                explanation: parsed.explanation || 'No explanation provided'
              };
            } else if ('rating' in parsed) {
              return {
                rating: Number(parsed.rating),
                explanation: parsed.explanation || 'No explanation provided'
              };
            }
          } else if (typeof parsed === 'number') {
            return {
              rating: parsed,
              explanation: 'No explanation provided'
            };
          }
        } catch (e) {
          // If parsing fails, try to use the string directly as a number
          const numRating = Number(ratingObj);
          if (!isNaN(numRating)) {
            return {
              rating: numRating,
              explanation: 'No explanation provided'
            };
          }
        }
      }
    }
    
    return null;
  };
  
  // Helper function to extract summary text from various possible structures
  const extractSummaryText = (data: any): string => {
    if (!data) return '';
    
    // Case 1: data is a string
    if (typeof data === 'string') {
      return data;
    }
    
    // Case 2: data is an object with summary property
    if (typeof data === 'object') {
      // Case 2.1: data.summary is a string
      if (data.summary && typeof data.summary === 'string') {
        return data.summary;
      }
      
      // Case 2.2: data.summary is an object with summary property
      if (data.summary && typeof data.summary === 'object' && data.summary.summary) {
        return typeof data.summary.summary === 'string' ? data.summary.summary : JSON.stringify(data.summary.summary);
      }
      
      // Case 2.3: data has a summary property that's an object but no nested summary
      if (data.summary && typeof data.summary === 'object') {
        return JSON.stringify(data.summary);
      }
    }
    
    // Fallback: stringify the entire data
    return typeof data === 'object' ? JSON.stringify(data) : String(data);
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-[#121212] rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#CECFD2]">Historical Search Results</h2>
              <Button
                onClick={onClose}
                size="sm"
                className="bg-[#4E46FF] hover:bg-[#3d37cc] text-white"
              >
                Close
              </Button>
            </div>
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4E46FF]"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-[#121212] rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#CECFD2]">Historical Search Results</h2>
              <Button
                onClick={onClose}
                size="sm"
                className="bg-[#4E46FF] hover:bg-[#3d37cc] text-white"
              >
                Close
              </Button>
            </div>
            <div className="flex items-center justify-center py-12 text-red-400">
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // If we're showing search history
  if (!processedData && searchHistory.length > 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-[#121212] rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#CECFD2]">Historical Searches</h2>
              <Button
                onClick={onClose}
                size="sm"
                className="bg-[#4E46FF] hover:bg-[#3d37cc] text-white"
              >
                Close
              </Button>
            </div>
            
            <div className="space-y-4">
              {/* Search history list */}
              {searchHistory.map((search) => (
                <div 
                  key={search.id}
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg border border-gray-700 cursor-pointer hover:bg-[#26222E]",
                    search.status === 'completed' ? "border-green-800 bg-[#26222E]" :
                    search.status === 'in_progress' ? "border-blue-800 bg-[#26222E]" :
                    search.status === 'failed' ? "border-red-800 bg-[#26222E]" :
                    search.status === 'cancelled' ? "border-amber-800 bg-[#26222E]" :
                    "border-gray-700 bg-[#26222E]"
                  )}
                  onClick={() => handleSearchSelect(search)}
                >
                  <div className="flex items-center gap-3">
                    {/* Overall Player Score Badge */}
                    {search.status === 'completed' && (
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
                        search.overall_player_score !== undefined ? (
                          search.overall_player_score >= 7 ? "bg-green-900 text-green-100" :
                          search.overall_player_score >= 4 ? "bg-yellow-900 text-yellow-100" :
                          "bg-red-900 text-red-100"
                        ) : "bg-gray-800 text-gray-400"
                      )}>
                        {search.overall_player_score !== undefined ? search.overall_player_score : "?"}
                      </div>
                    )}
                    
                    <div>
                      <p className="font-medium text-[#CECFD2]">
                        {search.player_name || `Player ${search.player_id}`}
                      </p>
                      <p className="text-sm text-[#CECFD2] opacity-70">
                        {new Date(search.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  {/* Only show status for non-completed searches */}
                  {search.status !== 'completed' && (
                    <div className="text-sm text-[#CECFD2] opacity-70">
                      {search.status === 'in_progress' ? 'In Progress' :
                       search.status === 'failed' ? 'Failed' :
                       search.status === 'cancelled' ? 'Cancelled' : 'Pending'}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 0}
                    className={page === 0 ? "opacity-50 bg-[#26222E] text-[#CECFD2] border-gray-700" : "bg-[#26222E] text-[#CECFD2] border-gray-700 hover:bg-[#3d37cc] hover:text-white"}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  
                  <span className="text-sm text-[#CECFD2]">
                    Page {page + 1} of {totalPages}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages - 1}
                    className={page >= totalPages - 1 ? "opacity-50 bg-[#26222E] text-[#CECFD2] border-gray-700" : "bg-[#26222E] text-[#CECFD2] border-gray-700 hover:bg-[#3d37cc] hover:text-white"}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // If no data to display
  if (!processedData && searchHistory.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-[#121212] rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#CECFD2]">Historical Searches</h2>
              <Button
                onClick={onClose}
                size="sm"
                className="bg-[#4E46FF] hover:bg-[#3d37cc] text-white"
              >
                Close
              </Button>
            </div>
            
            <div className="flex items-center justify-center py-12 text-[#CECFD2] opacity-70">
              <p>No historical searches found</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Render search results
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#121212] rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-[#CECFD2]">Historical Search Results</h2>
            <div className="flex items-center gap-2">
              {/* Back button if we came from search history */}
              {searchHistory.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setProcessedData(null);
                    setCurrentSearchData(null);
                  }}
                  className="bg-[#26222E] text-[#CECFD2] border-gray-700 hover:bg-[#3d37cc] hover:text-white"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back to List
                </Button>
              )}
              
              <Button 
                onClick={onClose} 
                size="sm"
                className="bg-[#4E46FF] hover:bg-[#3d37cc] text-white"
              >
                Close
              </Button>
            </div>
          </div>
          
          {/* Overall Player Score */}
          {processedData?.overall_player_score !== undefined && (
            <div className="mb-4 flex items-center justify-center">
              <div className="flex flex-col items-center">
                <p className="text-sm font-medium text-[#CECFD2] mb-1">
                  Overall Player Score
                </p>
                <div className={cn(
                  "w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold",
                  processedData.overall_player_score >= 7 ? "bg-green-900 text-green-100" :
                  processedData.overall_player_score >= 4 ? "bg-yellow-900 text-yellow-100" :
                  "bg-red-900 text-red-100"
                )}>
                  {processedData.overall_player_score}
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Define the section order to match KYPSearch.tsx */}
            {[
              'family',
              'financial',
              'career',
              'personal_life',
              'agent',
              'personality',
              'early_career_and_education',
              'adverse_media',
              'legal_and_criminal_history',
              'sponsorships'
            ].map(sectionId => {
              if (!processedData[sectionId]) return null;
              
              const data = processedData[sectionId];
              const summaryText = extractSummaryText(data);
              const ratingData = extractRatingData(data);
              
              return (
                <div key={sectionId} className="border border-gray-700 rounded-lg p-4 bg-[#121212]">
                  <h3 className="text-lg font-semibold mb-2 capitalize text-[#CECFD2]">
                    {sectionId.replace(/_/g, ' ')}
                  </h3>
                  
                  {/* Summary text with markdown support */}
                  <div className="text-sm text-[#CECFD2] prose prose-sm prose-invert max-w-none">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                    >
                      {summaryText}
                    </ReactMarkdown>
                  </div>
                  
                  {/* Section-specific article references - only show if there are actual references */}
                  {data.article_references && (
                    (() => {
                      // Check if article_references is a non-empty object
                      if (typeof data.article_references === 'object' && Object.keys(data.article_references).length > 0) {
                        return (
                          <div className="mt-4 pt-4 border-t border-gray-700">
                            <h4 className="text-sm font-medium text-[#CECFD2] mb-2">
                              Referenced Articles:
                            </h4>
                            <div className="space-y-2">
                              {Object.entries(data.article_references).map(([title, url], index) => (
                                <a 
                                  key={index}
                                  href={typeof url === 'string' ? url : '#'} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-start p-2 rounded-lg hover:bg-[#26222E] transition-colors"
                                >
                                  <div className="flex-shrink-0 w-4 h-4 mt-0.5 text-[#4E46FF]">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
                                      <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <span className="ml-2 text-sm text-[#4E46FF] hover:underline break-words">
                                    {title}
                                  </span>
                                </a>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      
                      // Check if article_references is a non-empty string that can be parsed as JSON
                      if (typeof data.article_references === 'string' && data.article_references.trim() !== '') {
                        try {
                          const parsedArticles = JSON.parse(data.article_references);
                          if (typeof parsedArticles === 'object' && Object.keys(parsedArticles).length > 0) {
                            return (
                              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Referenced Articles:
                                </h4>
                                <div className="space-y-2">
                                  <div className="p-2 rounded-md bg-gray-50 dark:bg-gray-700">
                                    <div className="space-y-2">
                                      {Object.entries(parsedArticles).map(([title, url], index) => (
                                        <a 
                                          key={index}
                                          href={typeof url === 'string' ? url : '#'} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="flex items-start p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                        >
                                          <div className="flex-shrink-0 w-4 h-4 mt-0.5 text-blue-500 dark:text-blue-400">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                              <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
                                              <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
                                            </svg>
                                          </div>
                                          <span className="ml-2 text-sm text-blue-600 dark:text-blue-400 hover:underline break-words">
                                            {title}
                                          </span>
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        } catch (e) {
                          // If the string is not empty and not JSON, show it as is
                          if (data.article_references.trim() !== '') {
                            return (
                              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Referenced Articles:
                                </h4>
                                <div className="space-y-2">
                                  <div className="p-2 rounded-md bg-gray-50 dark:bg-gray-700">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                      {data.article_references}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        }
                      }
                      
                      // Return null if there are no valid article references
                      return null;
                    })()
                  )}
                  
                  {/* Rating display */}
                  {ratingData && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="text-sm font-medium text-[#CECFD2]">
                          Rating:
                        </p>
                        <div className={cn(
                          "px-2 py-0.5 rounded text-sm font-medium",
                          ratingData.rating >= 7 ? "bg-green-900 text-green-100" :
                          ratingData.rating >= 4 ? "bg-yellow-900 text-yellow-100" :
                          "bg-red-900 text-red-100"
                        )}>
                          {ratingData.rating}
                        </div>
                        {ratingData.explanation && (
                          <div className="text-sm font-medium text-[#CECFD2] prose prose-sm prose-invert max-w-none">
                            <ReactMarkdown 
                              remarkPlugins={[remarkGfm]}
                            >
                              {`- ${ratingData.explanation}`}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Display any other sections not in the predefined order */}
            {Object.entries(processedData)
              .filter(([key, value]) => {
                // Log all keys to help debug the issue
                console.log('Section key:', key);
                
                // Exclude standard sections and any section that might be related to article references
                return !['status', 'message', 'player_id', 'overall_player_score', 'debug', 'article_references',
                  'family', 'financial', 'career', 'personal_life', 'agent', 'personality', 
                  'early_career_and_education', 'adverse_media', 'legal_and_criminal_history', 'sponsorships'].includes(key) &&
                  // Also exclude any key that contains "article" or "reference" in any case
                  !key.toLowerCase().includes('article') && 
                  !key.toLowerCase().includes('reference');
              })
              .map(([category, data]: [string, any]) => {
                // Extract summary and rating using helper functions
                const summaryText = extractSummaryText(data);
                const ratingData = extractRatingData(data);
                
                return (
                  <div key={category} className="border border-gray-700 rounded-lg p-4 bg-[#121212]">
                    <h3 className="text-lg font-semibold mb-2 capitalize text-[#CECFD2]">{category.replace(/_/g, ' ')}</h3>
                    
                    {/* Summary text */}
                    <div className="whitespace-pre-wrap text-sm text-[#CECFD2]">
                      {summaryText}
                    </div>
                    
                    {/* Section-specific article references - already checking for non-empty object */}
                    {data.article_references && Object.keys(data.article_references).length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <h4 className="text-sm font-medium text-[#CECFD2] mb-2">
                          Referenced Articles:
                        </h4>
                        <div className="grid grid-cols-1 gap-2">
                          {Object.entries(data.article_references).map(([title, url], index) => (
                            <a 
                              key={index}
                              href={typeof url === 'string' ? url : '#'} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-start p-2 rounded-lg hover:bg-[#26222E] transition-colors"
                            >
                              <div className="flex-shrink-0 w-4 h-4 mt-0.5 text-[#4E46FF]">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
                                  <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <span className="ml-2 text-sm text-[#4E46FF] hover:underline break-words">
                                {title}
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Rating display */}
                    {ratingData && (
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-sm font-medium text-[#CECFD2]">
                            Rating:
                          </p>
                          <div className={cn(
                            "px-2 py-0.5 rounded text-sm font-medium",
                            ratingData.rating >= 7 ? "bg-green-900 text-green-100" :
                            ratingData.rating >= 4 ? "bg-yellow-900 text-yellow-100" :
                            "bg-red-900 text-red-100"
                          )}>
                            {ratingData.rating}
                          </div>
                          {ratingData.explanation && (
                            <p className="text-sm font-medium text-[#CECFD2]">
                              - {ratingData.explanation}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              
            {/* All Referenced Articles Section - Moved to the very end */}
            {(processedData?._article_references_for_final_section || processedData?.debug?.summary?.all_articles) && (
              <div className="border border-gray-700 rounded-lg p-4 bg-[#121212]">
                <h3 className="text-lg font-semibold mb-4 text-[#CECFD2]">All Referenced Articles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* First check for debug.summary.all_articles */}
                  {processedData?.debug?.summary?.all_articles ? (
                    // Handle all_articles array from debug section
                    processedData.debug.summary.all_articles.map((article: any, index: number) => (
                      <a 
                        key={index}
                        href={article.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-start p-2 rounded-lg hover:bg-[#26222E] transition-colors"
                      >
                        <div className="flex-shrink-0 w-5 h-5 mt-0.5 text-[#4E46FF]">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
                            <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-2">
                          <span className="text-sm text-[#4E46FF] hover:underline break-words">
                            {article.title}
                          </span>
                          {article.category && (
                            <div className="text-xs text-[#CECFD2] opacity-70 mt-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#26222E] text-[#CECFD2]">
                                {article.category}
                              </span>
                              {article.from_database && (
                                <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded text-xs font-medium bg-[#26222E] text-[#4E46FF]">
                                  From Database
                                </span>
                              )}
                              {article.historical && (
                                <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded text-xs font-medium bg-[#26222E] text-purple-300">
                                  Historical
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </a>
                    ))
                  ) : typeof processedData?._article_references_for_final_section === 'string' ? (
                    // Handle case where article references is a JSON string
                    <div className="col-span-2 p-2 rounded-md bg-gray-50 dark:bg-gray-700">
                      {(() => {
                        try {
                          const parsedArticles = JSON.parse(processedData._article_references_for_final_section);
                          return (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {Object.entries(parsedArticles).map(([title, url], index) => (
                                <a 
                                  key={index}
                                  href={typeof url === 'string' ? url : '#'} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-start p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                >
                                  <div className="flex-shrink-0 w-5 h-5 mt-0.5 text-blue-500 dark:text-blue-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
                                      <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <span className="ml-2 text-sm text-blue-600 dark:text-blue-400 hover:underline break-words">
                                    {title}
                                  </span>
                                </a>
                              ))}
                            </div>
                          );
                        } catch (e) {
                          return (
                            <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                              {processedData._article_references_for_final_section}
                            </div>
                          );
                        }
                      })()}
                    </div>
                  ) : processedData?._article_references_for_final_section ? (
                    // Handle case where article references is an object
                    Object.entries(processedData._article_references_for_final_section).map(([title, url], index) => (
                      <a 
                        key={index}
                        href={typeof url === 'string' ? url : '#'} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-start p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex-shrink-0 w-5 h-5 mt-0.5 text-blue-500 dark:text-blue-400">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
                            <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="ml-2 text-sm text-blue-600 dark:text-blue-400 hover:underline break-words">
                          {title}
                        </span>
                      </a>
                    ))
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};